import React from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from "@/lib/utils";
import { NewPageFormState } from "./types";
import { PageBasicsStep, ContentPreferencesStep, ClarifyingQuestionsStep } from "./steps";

interface StepValidation {
  isValid: boolean;
  canProceed: boolean;
  errors: string[];
}

interface StepNavigation {
  currentStep: number;
  totalSteps: number;
  canGoNext: boolean;
  canGoPrevious: boolean;
  canSubmit: boolean;
  goToNext: () => void;
  goToPrevious: () => void;
  goToStep: (step: number) => void;
  getStepValidation: (step: number) => StepValidation;
}

interface NewPageModalStepperProps {
  value: NewPageFormState;
  onChange: (value: NewPageFormState) => void;
  workbookId: string;
  showValidation?: boolean;
  stepNavigation: StepNavigation;
  goalThreadId?: string;
}

export const NewPageModalStepper: React.FC<NewPageModalStepperProps> = ({
  value,
  onChange,
  workbookId,
  showValidation = false,
  stepNavigation,
  goalThreadId
}) => {
  const { currentStep, totalSteps } = stepNavigation;

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      <div className="flex items-center space-x-2">
        {Array.from({ length: totalSteps }, (_, index) => {
          const stepNumber = index + 1;
          const isActive = stepNumber === currentStep;
          const isCompleted = stepNumber < currentStep;
          const stepValidation = stepNavigation.getStepValidation(stepNumber);
          
          return (
            <div key={stepNumber} className="flex items-center">
              {/* Step Circle */}
              <motion.div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-purple-600 text-white ring-4 ring-purple-600/20"
                    : isCompleted && stepValidation.isValid
                      ? "bg-green-500 text-white"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-500"
                )}
                whileHover={{ scale: 1.05 }}
              >
                {isCompleted && stepValidation.isValid ? "✓" : stepNumber}
              </motion.div>
              
              {/* Step Connector */}
              {stepNumber < totalSteps && (
                <div
                  className={cn(
                    "w-12 h-0.5 mx-2 transition-colors duration-200",
                    isCompleted && stepValidation.isValid
                      ? "bg-green-500"
                      : "bg-gray-200 dark:bg-gray-700"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PageBasicsStep
            value={value}
            onChange={onChange}
            showValidation={showValidation}
          />
        );
      case 2:
        return (
          <ContentPreferencesStep
            value={value}
            onChange={onChange}
            workbookId={workbookId}
          />
        );
      // NOTE: Clarifying Questions moved to step 2 (ContentPreferencesStep)
      // Research functionality will be step 3 when re-enabled
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      {renderStepIndicator()}

      {/* Step Content */}
      <div className="min-h-[400px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// Export the step navigation hook for use in parent components
export { useStepNavigation } from "./hooks/useStepNavigation"; 